import { useMemo, useState } from "react";
import { Transaction } from "@common/types/transaction";
import { UpdateTransactionSplitV2Dto } from "@common/dto/transactions/update-transaction-split-v2.dto";
import { UpdateTransactionSplitsV2Dto } from "@common/dto/transactions/update-transaction-splits-v2.dto";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { didRequestReachServer } from "@api/apiError";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { useApplyScheduleTemplateMutation } from "@api/scheduleTemplate.api";
import { useScheduleLockedPeriodsDialog } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useUpdateTransactionSplitsV2 } from "@hooks/useUpdateTransactionSplitsV2";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { removeJournalEntryQueries } from "@components/accounting/journalEntryDrawer/journalEntryQuery";
import {
    removeTransactionDetailsQueries,
    useTransactionDetailsQuery,
} from "../TransactionDetails/useTransactionDetailsQuery";
import { invalidateTransactionsQueries } from "../useTransactionsQuery";
import { applySplitSchedules } from "./applySplitSchedules";
import { TransactionSplitContainer } from "./TransactionSplitContainer";
import {
    TransactionSplitFormRow,
    getSplitAccountBaselines,
    toSplitDto,
} from "./transactionSplitUtils";
import styles from "./TransactionSplitModal.module.scss";
import { AmountDisplayFormat } from "./TransactionSplitCard.lib";
import { TransactionSplitCard } from "./TransactionSplitCard";

interface Props {
    transaction: TransactionListItem;
    closeModal: () => void;
    onSplitSave?: (savedTransactions: Transaction[]) => void;
    open: boolean;
}

export const TransactionSplitModal: React.FC<Props> = ({
    transaction: originalTransaction,
    onSplitSave,
    closeModal,
    open,
}: Props) => {
    const [showUnsplitConfirmation, setShowUnsplitConfirmation] =
        useState(false);

    const { transaction } = useTransactionDetailsQuery(
        originalTransaction?.splitParentId ?? originalTransaction.id,
    );

    const applyScheduleMutation = useApplyScheduleTemplateMutation();
    const showLockedPeriods = useScheduleLockedPeriodsDialog();
    const updateSplitsMutation = useUpdateTransactionSplitsV2();
    const { error: toastError } = useToaster();
    const entities = useEntities({ excludeMockEntities: true });
    const { enabled: isGlFirst } = useIsGlFirst();
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const { data: ledgersByEntityId, isPending: isLedgersPending } =
        useLedgersByEntityIds(entityIds);

    const finishSplitsSave = (saved: Transaction) => {
        onSplitSave?.(saved.splits ?? []);
        invalidateTransactionsQueries();
        removeTransactionDetailsQueries();
        removeJournalEntryQueries();
        closeModal();
    };

    const handleFormSubmit = async (values: {
        splits: TransactionSplitFormRow[];
    }) => {
        if (!transaction) {
            return;
        }
        const dto: UpdateTransactionSplitsV2Dto = {
            splits: values.splits.map((split) =>
                toSplitDto({
                    split,
                    ledgers: ledgersByEntityId?.[split.entityId] ?? [],
                    ...(isGlFirst
                        ? {}
                        : getSplitAccountBaselines({
                              split,
                              transaction,
                              ledgersByEntityId: ledgersByEntityId ?? {},
                          })),
                }),
            ),
        };

        try {
            const saved = await updateSplitsMutation.mutateAsync({
                transactionId: transaction.id,
                dto,
            });
            try {
                await applySplitSchedules({
                    formSplits: values.splits,
                    savedTransaction: saved,
                    apply: applyScheduleMutation.mutateAsync,
                });
            } catch (applyError) {
                if (!showLockedPeriods(applyError)) {
                    toastError(
                        "Failed to apply schedule to one or more splits",
                    );
                }
            }
            finishSplitsSave(saved);
        } catch (error) {
            if (!didRequestReachServer(error)) {
                toastError("Failed to save split");
            }
        }
    };

    const handleUnsplit = async () => {
        if (!transaction) {
            return;
        }
        const splits: UpdateTransactionSplitV2Dto[] = [
            {
                id: transaction.id,
                amount: transaction.originalAmount,
                entityId: transaction.entity.id,
                splitDescription: transaction.displayDescription ?? "",
                categoryId: transaction.categoryId,
            },
        ];

        try {
            const saved = await updateSplitsMutation.mutateAsync({
                transactionId: transaction.id,
                dto: { splits },
            });
            setShowUnsplitConfirmation(false);
            finishSplitsSave(saved);
        } catch (error) {
            setShowUnsplitConfirmation(false);
            if (!didRequestReachServer(error)) {
                toastError("Failed to unsplit transaction");
            }
        }
    };

    return (
        <>
            <ConfirmationModal
                title="Are you sure that you want to unsplit these transactions?"
                question=""
                show={showUnsplitConfirmation && open}
                onConfirm={handleUnsplit}
                onReject={() => setShowUnsplitConfirmation(false)}
                onHide={() => setShowUnsplitConfirmation(false)}
                className={styles.unsplitModal}
            />

            <StandardModal
                size="xl"
                dialogClassName={styles.dialogWide}
                show={
                    Boolean(originalTransaction) &&
                    !showUnsplitConfirmation &&
                    open
                }
                onHide={closeModal}
            >
                <StandardModalHeader>Split transaction</StandardModalHeader>

                <StandardModalBody className={styles.body}>
                    {transaction && !isLedgersPending ? (
                        <>
                            <div className="px-4 py-2">
                                <TransactionSplitCard
                                    className={styles.transaction}
                                    transaction={{
                                        ...transaction,
                                        amount: transaction.originalAmount,
                                    }}
                                    amountDisplayFormat={
                                        AmountDisplayFormat.SIGN
                                    }
                                    showEntity
                                />
                            </div>
                            <TransactionSplitContainer
                                transaction={transaction}
                                ledgersByEntityId={ledgersByEntityId ?? {}}
                                onSubmit={handleFormSubmit}
                                onUnsplit={() =>
                                    setShowUnsplitConfirmation(true)
                                }
                                loading={
                                    updateSplitsMutation.isPending ||
                                    applyScheduleMutation.isPending
                                }
                            />
                        </>
                    ) : (
                        <Loader />
                    )}
                </StandardModalBody>
            </StandardModal>
        </>
    );
};
